import type { SupportedLocale } from '@erp/shared';

export const warehouseImportTranslations = {
  de: {
    title: 'Lagerimport',
    nav: 'Import',
    description: 'Lagerbestand aus einer Excel-Datei zu vorhandenen Artikeln hinzufügen.',
    help: 'Hilfe zum Lagerimport',
    download: 'Vorlage herunterladen',
    downloading: 'Vorlage wird geladen …',
    templateHint:
      'Eine XLSX-Datei mit genau einem Tabellenblatt. Artikelnummer und Menge sind Pflicht; Gebinde, Charge, MHD, Lager, Lagerplatz, Palet No und REF_EXT stehen in derselben Zeile.',
    file: 'Excel-Datei',
    check: 'Datei prüfen',
    checking: 'Datei wird geprüft …',
    empty:
      'Laden Sie die Vorlage herunter, ersetzen Sie die Musterzeile und prüfen Sie Ihre Datei.',
    preview: 'Vorschau',
    fileLabel: 'Datei: {{name}}',
    validRows: 'Gültige Zeilen: {{count}}',
    errorRows: 'Fehlerhafte Zeilen: {{count}}',
    row: 'Zeile {{number}}',
    article: 'Artikel',
    quantity: 'Menge / Gebinde',
    conversion: 'Umrechnung in Basiseinheit',
    batch: 'Charge',
    bestBefore: 'MHD',
    location: 'Lager / Lagerplatz',
    additional: 'Chargen-Zusatzfelder',
    status: 'Prüfung',
    valid: 'Gültig',
    fallback: 'Basiseinheit als Ersatz (Faktor 1)',
    fallbackHint:
      'Ein leeres oder unbekanntes Gebinde verwendet Faktor 1. Die Verkaufsvorgabe des Artikels wird nicht verwendet.',
    confirm:
      'Ich habe alle Zeilen und Umrechnungen geprüft. Die Mengen werden zum Bestand addiert.',
    execute: 'Bestand hinzufügen',
    executing: 'Bestand wird hinzugefügt …',
    success: 'Bestand für {{count}} Zeilen hinzugefügt.',
    success_one: 'Bestand für {{count}} Zeile hinzugefügt.',
    alreadyImported:
      'Diese Datei wurde bereits importiert. Es wird kein Bestand erneut hinzugefügt.',
    blocked:
      'Korrigieren Sie alle fehlerhaften Zeilen in der Datei und prüfen Sie sie erneut. Es wird keine Teilmenge gebucht.',
    readOnly:
      'Sie können gespeicherte Vorschauen lesen. Zum Prüfen und Buchen ist das Recht zur Lagerbearbeitung erforderlich.',
    retry: 'Erneut versuchen',
    newFile: 'Andere Datei prüfen',
    previous: 'Zurück',
    next: 'Weiter',
    page: 'Seite {{current}} von {{total}}',
    loading: 'Vorschau wird geladen',
    loadError: 'Die gespeicherte Vorschau konnte nicht geladen werden.',
    uploadError:
      'Die Datei konnte nicht geprüft werden. Prüfen Sie das Format und versuchen Sie es erneut.',
    executeError:
      'Die Buchung konnte nicht bestätigt werden. Prüfen Sie den angezeigten Stand und versuchen Sie es erneut. Eine Wiederholung bucht nicht doppelt.',
    downloadError: 'Die Vorlage konnte nicht heruntergeladen werden.',
    genericError: 'Prüfen Sie diese Zeile und laden Sie die korrigierte Datei erneut hoch.',
    sampleHint:
      'Die vollständig ausgefüllte Zeile mit MUSTER ist nur ein Beispiel. Ersetzen oder löschen Sie sie vor dem Import.',
    errors: {
      articleRequired: 'Artikelnummer fehlt.',
      articleUnknown: 'Artikelnummer ist unbekannt. Es werden keine Artikel angelegt.',
      exampleRow: 'Ersetzen oder löschen Sie die Musterzeile.',
      stockNotManaged: 'Dieser Artikel wird nicht lagergeführt.',
      batchRequired: 'Dieser Artikel benötigt eine Chargennummer.',
      batchNotManaged: 'Dieser Artikel unterstützt keine Charge.',
      mhdRequired: 'Dieser Artikel benötigt ein MHD.',
      mhdInvalid: 'MHD ist ungültig. Verwenden Sie YYYY-MM-DD.',
      quantityInvalid: 'Menge muss eine positive Zahl sein.',
      quantityPrecision: 'Menge hat zu viele Nachkommastellen.',
      quantityOverflow: 'Die umgerechnete Menge ist zu groß.',
      locationInvalid: 'Lager oder Lagerplatz ist ungültig.',
      customFieldMissing: 'Ein benötigtes Chargen-Zusatzfeld fehlt.',
      customFieldInvalid: 'Ein Chargen-Zusatzfeld ist ungültig.',
      customFieldForbidden: 'Sie dürfen dieses Chargen-Zusatzfeld nicht bearbeiten.',
      batchConflict: 'Die Angaben widersprechen einer vorhandenen Charge.',
      duplicateBatchConflict:
        'Mehrere Zeilen enthalten widersprüchliche Angaben zur selben Charge.',
      inventoryFrozen: 'Eine laufende Inventur sperrt diesen Artikel.',
      metadataChanged:
        'Artikel-, Chargen- oder Zusatzfeldangaben haben sich geändert. Prüfen Sie die Datei erneut.',
      previewInvalid: 'Die Vorschau enthält Fehler.',
      previewChanged: 'Die Vorschau ist nicht mehr aktuell. Prüfen Sie die Datei erneut.',
      alreadyImported: 'Diese Datei wurde bereits importiert.',
      fileInvalid: 'Verwenden Sie eine gültige XLSX-Datei.',
      fileTooLarge: 'Die Datei ist zu groß.',
      tooManyRows: 'Die Datei enthält zu viele Zeilen.',
      headerInvalid: 'Die Spaltenüberschriften sind ungültig.',
      fileEmpty: 'Die Datei enthält keine Datenzeilen.',
      formulaNotAllowed: 'Formeln sind nicht erlaubt. Verwenden Sie feste Werte.',
      invalidFile: 'Verwenden Sie eine gültige XLSX-Datei.',
      worksheetCount: 'Die Datei muss genau ein Tabellenblatt enthalten.',
      requiredColumns: 'Die Spalten Artikelnummer und Menge sind erforderlich.',
      duplicateColumn: 'Eine Spaltenüberschrift ist doppelt vorhanden.',
      unknownColumn: 'Eine Spaltenüberschrift ist unbekannt.',
      missingColumnHeader: 'Eine befüllte Spalte hat keine Überschrift.',
      emptyFile: 'Die Datei enthält keine Datenzeilen.',
    },
  },
  en: {
    title: 'Warehouse import',
    nav: 'Import',
    description: 'Add warehouse stock for existing products from an Excel file.',
    help: 'Warehouse import help',
    download: 'Download template',
    downloading: 'Downloading template …',
    templateHint:
      'One XLSX file with exactly one worksheet. Article number and quantity are required; packaging, batch, best-before date, warehouse, location, Palet No and REF_EXT belong in the same row.',
    file: 'Excel file',
    check: 'Check file',
    checking: 'Checking file …',
    empty: 'Download the template, replace the sample row and check your file.',
    preview: 'Preview',
    fileLabel: 'File: {{name}}',
    validRows: 'Valid rows: {{count}}',
    errorRows: 'Rows with errors: {{count}}',
    row: 'Row {{number}}',
    article: 'Product',
    quantity: 'Quantity / packaging',
    conversion: 'Base unit conversion',
    batch: 'Batch',
    bestBefore: 'Best before',
    location: 'Warehouse / location',
    additional: 'Batch custom fields',
    status: 'Validation',
    valid: 'Valid',
    fallback: 'Base unit fallback (factor 1)',
    fallbackHint:
      'Empty or unknown packaging uses factor 1. The product’s sales default is never used.',
    confirm: 'I have reviewed all rows and conversions. The quantities will be added to stock.',
    execute: 'Add stock',
    executing: 'Adding stock …',
    success: 'Stock added for {{count}} rows.',
    success_one: 'Stock added for {{count}} row.',
    alreadyImported: 'This file has already been imported. No stock will be added again.',
    blocked:
      'Correct every invalid row in the file and check it again. No partial stock posting is allowed.',
    readOnly:
      'You can read saved previews. Checking and posting require inventory write permission.',
    retry: 'Retry',
    newFile: 'Check another file',
    previous: 'Previous',
    next: 'Next',
    page: 'Page {{current}} of {{total}}',
    loading: 'Loading preview',
    loadError: 'The saved preview could not be loaded.',
    uploadError: 'The file could not be checked. Check its format and try again.',
    executeError:
      'The posting could not be confirmed. Review the displayed status and try again. Retrying will not add stock twice.',
    downloadError: 'The template could not be downloaded.',
    genericError: 'Check this row and upload the corrected file again.',
    sampleHint:
      'The fully completed row marked MUSTER is only a sample. Replace or remove it before importing.',
    errors: {
      articleRequired: 'Article number is required.',
      articleUnknown: 'The article number is unknown. No products will be created.',
      exampleRow: 'Replace or remove the sample row.',
      stockNotManaged: 'Stock is not managed for this product.',
      batchRequired: 'This product requires a batch number.',
      batchNotManaged: 'This product does not support batches.',
      mhdRequired: 'This product requires a best-before date.',
      mhdInvalid: 'The best-before date is invalid. Use YYYY-MM-DD.',
      quantityInvalid: 'Quantity must be a positive number.',
      quantityPrecision: 'Quantity has too many decimal places.',
      quantityOverflow: 'The converted quantity is too large.',
      locationInvalid: 'The warehouse or storage location is invalid.',
      customFieldMissing: 'A required batch custom field is missing.',
      customFieldInvalid: 'A batch custom field is invalid.',
      customFieldForbidden: 'You cannot edit this batch custom field.',
      batchConflict: 'These details conflict with an existing batch.',
      duplicateBatchConflict: 'Several rows contain conflicting details for the same batch.',
      inventoryFrozen: 'An active inventory count has frozen this product.',
      metadataChanged: 'Product, batch or custom field details changed. Check the file again.',
      previewInvalid: 'The preview contains errors.',
      previewChanged: 'The preview is out of date. Check the file again.',
      alreadyImported: 'This file has already been imported.',
      fileInvalid: 'Use a valid XLSX file.',
      fileTooLarge: 'The file is too large.',
      tooManyRows: 'The file contains too many rows.',
      headerInvalid: 'The column headers are invalid.',
      fileEmpty: 'The file has no data rows.',
      formulaNotAllowed: 'Formulas are not allowed. Use fixed values.',
      invalidFile: 'Use a valid XLSX file.',
      worksheetCount: 'The file must contain exactly one worksheet.',
      requiredColumns: 'Article number and quantity columns are required.',
      duplicateColumn: 'A column header is duplicated.',
      unknownColumn: 'A column header is unknown.',
      missingColumnHeader: 'A populated column has no header.',
      emptyFile: 'The file has no data rows.',
    },
  },
  fr: {
    title: 'Import de stock',
    nav: 'Import',
    description: 'Ajouter du stock aux articles existants depuis un fichier Excel.',
    help: 'Aide à l’import de stock',
    download: 'Télécharger le modèle',
    downloading: 'Téléchargement du modèle …',
    templateHint:
      'Un fichier XLSX avec une seule feuille. Numéro d’article et quantité obligatoires ; conditionnement, lot, DDM, entrepôt, emplacement, Palet No et REF_EXT figurent sur la même ligne.',
    file: 'Fichier Excel',
    check: 'Vérifier le fichier',
    checking: 'Vérification du fichier …',
    empty: 'Téléchargez le modèle, remplacez la ligne exemple et vérifiez votre fichier.',
    preview: 'Aperçu',
    fileLabel: 'Fichier : {{name}}',
    validRows: 'Lignes valides : {{count}}',
    errorRows: 'Lignes en erreur : {{count}}',
    row: 'Ligne {{number}}',
    article: 'Article',
    quantity: 'Quantité / conditionnement',
    conversion: 'Conversion en unité de base',
    batch: 'Lot',
    bestBefore: 'DDM',
    location: 'Entrepôt / emplacement',
    additional: 'Champs personnalisés du lot',
    status: 'Vérification',
    valid: 'Valide',
    fallback: 'Unité de base par défaut (facteur 1)',
    fallbackHint:
      'Un conditionnement vide ou inconnu utilise le facteur 1. Le conditionnement de vente par défaut n’est jamais utilisé.',
    confirm:
      'J’ai vérifié toutes les lignes et conversions. Les quantités seront ajoutées au stock.',
    execute: 'Ajouter au stock',
    executing: 'Ajout au stock …',
    success: 'Stock ajouté pour {{count}} lignes.',
    success_one: 'Stock ajouté pour {{count}} ligne.',
    alreadyImported: 'Ce fichier a déjà été importé. Aucun stock ne sera ajouté à nouveau.',
    blocked:
      'Corrigez toutes les lignes en erreur puis vérifiez le fichier à nouveau. Aucune comptabilisation partielle n’est autorisée.',
    readOnly:
      'Vous pouvez lire les aperçus enregistrés. La vérification et la comptabilisation nécessitent le droit de modification du stock.',
    retry: 'Réessayer',
    newFile: 'Vérifier un autre fichier',
    previous: 'Précédent',
    next: 'Suivant',
    page: 'Page {{current}} sur {{total}}',
    loading: 'Chargement de l’aperçu',
    loadError: 'L’aperçu enregistré n’a pas pu être chargé.',
    uploadError: 'Le fichier n’a pas pu être vérifié. Vérifiez son format et réessayez.',
    executeError:
      'La comptabilisation n’a pas pu être confirmée. Vérifiez le statut affiché et réessayez. Une nouvelle tentative ne double pas le stock.',
    downloadError: 'Le modèle n’a pas pu être téléchargé.',
    genericError: 'Vérifiez cette ligne et téléversez le fichier corrigé.',
    sampleHint:
      'La ligne entièrement remplie marquée MUSTER est un exemple. Remplacez-la ou supprimez-la avant l’import.',
    errors: {
      articleRequired: 'Le numéro d’article est obligatoire.',
      articleUnknown: 'Numéro d’article inconnu. Aucun article ne sera créé.',
      exampleRow: 'Remplacez ou supprimez la ligne exemple.',
      stockNotManaged: 'Le stock n’est pas géré pour cet article.',
      batchRequired: 'Cet article nécessite un numéro de lot.',
      batchNotManaged: 'Cet article ne prend pas en charge les lots.',
      mhdRequired: 'Cet article nécessite une DDM.',
      mhdInvalid: 'DDM invalide. Utilisez YYYY-MM-DD.',
      quantityInvalid: 'La quantité doit être un nombre positif.',
      quantityPrecision: 'La quantité comporte trop de décimales.',
      quantityOverflow: 'La quantité convertie est trop grande.',
      locationInvalid: 'Entrepôt ou emplacement invalide.',
      customFieldMissing: 'Un champ personnalisé obligatoire du lot manque.',
      customFieldInvalid: 'Un champ personnalisé du lot est invalide.',
      customFieldForbidden: 'Vous ne pouvez pas modifier ce champ personnalisé du lot.',
      batchConflict: 'Ces informations contredisent un lot existant.',
      duplicateBatchConflict:
        'Plusieurs lignes contiennent des informations contradictoires pour le même lot.',
      inventoryFrozen: 'Un inventaire en cours bloque cet article.',
      metadataChanged:
        'Les informations d’article, de lot ou de champ personnalisé ont changé. Vérifiez le fichier à nouveau.',
      previewInvalid: 'L’aperçu contient des erreurs.',
      previewChanged: 'L’aperçu est périmé. Vérifiez le fichier à nouveau.',
      alreadyImported: 'Ce fichier a déjà été importé.',
      fileInvalid: 'Utilisez un fichier XLSX valide.',
      fileTooLarge: 'Le fichier est trop volumineux.',
      tooManyRows: 'Le fichier contient trop de lignes.',
      headerInvalid: 'Les en-têtes de colonnes sont invalides.',
      fileEmpty: 'Le fichier ne contient aucune ligne de données.',
      formulaNotAllowed: 'Les formules sont interdites. Utilisez des valeurs fixes.',
      invalidFile: 'Utilisez un fichier XLSX valide.',
      worksheetCount: 'Le fichier doit contenir une seule feuille.',
      requiredColumns: 'Les colonnes numéro d’article et quantité sont obligatoires.',
      duplicateColumn: 'Un en-tête de colonne est en double.',
      unknownColumn: 'Un en-tête de colonne est inconnu.',
      missingColumnHeader: 'Une colonne remplie n’a pas d’en-tête.',
      emptyFile: 'Le fichier ne contient aucune ligne de données.',
    },
  },
  es: {
    title: 'Importación de existencias',
    nav: 'Importar',
    description: 'Añada existencias a artículos existentes desde un archivo Excel.',
    help: 'Ayuda de importación de existencias',
    download: 'Descargar plantilla',
    downloading: 'Descargando plantilla …',
    templateHint:
      'Un archivo XLSX con una sola hoja. Número de artículo y cantidad obligatorios; envase, lote, consumo preferente, almacén, ubicación, Palet No y REF_EXT van en la misma fila.',
    file: 'Archivo Excel',
    check: 'Comprobar archivo',
    checking: 'Comprobando archivo …',
    empty: 'Descargue la plantilla, sustituya la fila de ejemplo y compruebe su archivo.',
    preview: 'Vista previa',
    fileLabel: 'Archivo: {{name}}',
    validRows: 'Filas válidas: {{count}}',
    errorRows: 'Filas con errores: {{count}}',
    row: 'Fila {{number}}',
    article: 'Artículo',
    quantity: 'Cantidad / envase',
    conversion: 'Conversión a unidad base',
    batch: 'Lote',
    bestBefore: 'Consumo preferente',
    location: 'Almacén / ubicación',
    additional: 'Campos adicionales del lote',
    status: 'Comprobación',
    valid: 'Válida',
    fallback: 'Unidad base alternativa (factor 1)',
    fallbackHint:
      'Un envase vacío o desconocido utiliza factor 1. Nunca se utiliza el envase de venta predeterminado del artículo.',
    confirm:
      'He revisado todas las filas y conversiones. Las cantidades se añadirán a las existencias.',
    execute: 'Añadir existencias',
    executing: 'Añadiendo existencias …',
    success: 'Existencias añadidas para {{count}} filas.',
    success_one: 'Existencias añadidas para {{count}} fila.',
    alreadyImported: 'Este archivo ya se ha importado. No se añadirán existencias de nuevo.',
    blocked:
      'Corrija todas las filas con errores y vuelva a comprobar el archivo. No se permite contabilizar parcialmente.',
    readOnly:
      'Puede leer vistas previas guardadas. Comprobar y contabilizar requieren permiso de edición de existencias.',
    retry: 'Reintentar',
    newFile: 'Comprobar otro archivo',
    previous: 'Anterior',
    next: 'Siguiente',
    page: 'Página {{current}} de {{total}}',
    loading: 'Cargando vista previa',
    loadError: 'No se pudo cargar la vista previa guardada.',
    uploadError: 'No se pudo comprobar el archivo. Revise su formato y vuelva a intentarlo.',
    executeError:
      'No se pudo confirmar la contabilización. Revise el estado mostrado y vuelva a intentarlo. Reintentar no duplica las existencias.',
    downloadError: 'No se pudo descargar la plantilla.',
    genericError: 'Revise esta fila y vuelva a subir el archivo corregido.',
    sampleHint:
      'La fila completamente rellenada marcada MUSTER es solo un ejemplo. Sustitúyala o elimínela antes de importar.',
    errors: {
      articleRequired: 'El número de artículo es obligatorio.',
      articleUnknown: 'Número de artículo desconocido. No se crearán artículos.',
      exampleRow: 'Sustituya o elimine la fila de ejemplo.',
      stockNotManaged: 'Este artículo no gestiona existencias.',
      batchRequired: 'Este artículo requiere un número de lote.',
      batchNotManaged: 'Este artículo no admite lotes.',
      mhdRequired: 'Este artículo requiere fecha de consumo preferente.',
      mhdInvalid: 'Fecha de consumo preferente inválida. Use YYYY-MM-DD.',
      quantityInvalid: 'La cantidad debe ser un número positivo.',
      quantityPrecision: 'La cantidad tiene demasiados decimales.',
      quantityOverflow: 'La cantidad convertida es demasiado grande.',
      locationInvalid: 'Almacén o ubicación inválidos.',
      customFieldMissing: 'Falta un campo adicional obligatorio del lote.',
      customFieldInvalid: 'Un campo adicional del lote es inválido.',
      customFieldForbidden: 'No puede editar este campo adicional del lote.',
      batchConflict: 'Estos datos contradicen un lote existente.',
      duplicateBatchConflict: 'Varias filas contienen datos contradictorios para el mismo lote.',
      inventoryFrozen: 'Un inventario en curso bloquea este artículo.',
      metadataChanged:
        'Han cambiado los datos del artículo, lote o campo adicional. Compruebe el archivo de nuevo.',
      previewInvalid: 'La vista previa contiene errores.',
      previewChanged: 'La vista previa está desactualizada. Compruebe el archivo de nuevo.',
      alreadyImported: 'Este archivo ya se ha importado.',
      fileInvalid: 'Utilice un archivo XLSX válido.',
      fileTooLarge: 'El archivo es demasiado grande.',
      tooManyRows: 'El archivo contiene demasiadas filas.',
      headerInvalid: 'Las cabeceras de columna son inválidas.',
      fileEmpty: 'El archivo no contiene filas de datos.',
      formulaNotAllowed: 'No se permiten fórmulas. Utilice valores fijos.',
      invalidFile: 'Utilice un archivo XLSX válido.',
      worksheetCount: 'El archivo debe contener una sola hoja.',
      requiredColumns: 'Las columnas número de artículo y cantidad son obligatorias.',
      duplicateColumn: 'Una cabecera de columna está duplicada.',
      unknownColumn: 'Una cabecera de columna es desconocida.',
      missingColumnHeader: 'Una columna con datos no tiene cabecera.',
      emptyFile: 'El archivo no contiene filas de datos.',
    },
  },
  tr: {
    title: 'Depo içe aktarımı',
    nav: 'İçe aktar',
    description: 'Excel dosyasından mevcut ürünlere depo stoğu ekleyin.',
    help: 'Depo içe aktarımı yardımı',
    download: 'Şablonu indir',
    downloading: 'Şablon indiriliyor …',
    templateHint:
      'Tam olarak bir çalışma sayfası içeren XLSX dosyası. Ürün numarası ve miktar zorunludur; ambalaj, parti, tüketim tarihi, depo, konum, Palet No ve REF_EXT aynı satırda bulunur.',
    file: 'Excel dosyası',
    check: 'Dosyayı kontrol et',
    checking: 'Dosya kontrol ediliyor …',
    empty: 'Şablonu indirin, örnek satırı değiştirin ve dosyanızı kontrol edin.',
    preview: 'Önizleme',
    fileLabel: 'Dosya: {{name}}',
    validRows: 'Geçerli satırlar: {{count}}',
    errorRows: 'Hatalı satırlar: {{count}}',
    row: 'Satır {{number}}',
    article: 'Ürün',
    quantity: 'Miktar / ambalaj',
    conversion: 'Temel birime dönüşüm',
    batch: 'Parti',
    bestBefore: 'Tavsiye edilen tüketim tarihi',
    location: 'Depo / konum',
    additional: 'Parti ek alanları',
    status: 'Kontrol',
    valid: 'Geçerli',
    fallback: 'Temel birim yedeği (faktör 1)',
    fallbackHint:
      'Boş veya bilinmeyen ambalaj için faktör 1 kullanılır. Ürünün varsayılan satış ambalajı kullanılmaz.',
    confirm: 'Tüm satırları ve dönüşümleri kontrol ettim. Miktarlar stoğa eklenecek.',
    execute: 'Stok ekle',
    executing: 'Stok ekleniyor …',
    success: '{{count}} satır için stok eklendi.',
    success_one: '{{count}} satır için stok eklendi.',
    alreadyImported: 'Bu dosya zaten içe aktarıldı. Tekrar stok eklenmez.',
    blocked:
      'Dosyadaki tüm hatalı satırları düzeltin ve tekrar kontrol edin. Kısmi kayıt yapılamaz.',
    readOnly:
      'Kayıtlı önizlemeleri okuyabilirsiniz. Kontrol ve kayıt için stok düzenleme yetkisi gerekir.',
    retry: 'Tekrar dene',
    newFile: 'Başka dosya kontrol et',
    previous: 'Önceki',
    next: 'Sonraki',
    page: 'Sayfa {{current}} / {{total}}',
    loading: 'Önizleme yükleniyor',
    loadError: 'Kayıtlı önizleme yüklenemedi.',
    uploadError: 'Dosya kontrol edilemedi. Biçimi kontrol edip tekrar deneyin.',
    executeError:
      'Kayıt onaylanamadı. Görüntülenen durumu kontrol edip tekrar deneyin. Tekrar denemek stoğu iki kez eklemez.',
    downloadError: 'Şablon indirilemedi.',
    genericError: 'Bu satırı kontrol edip düzeltilmiş dosyayı yeniden yükleyin.',
    sampleHint:
      'MUSTER işaretli tamamen doldurulmuş satır yalnızca örnektir. İçe aktarmadan önce değiştirin veya silin.',
    errors: {
      articleRequired: 'Ürün numarası zorunludur.',
      articleUnknown: 'Ürün numarası bilinmiyor. Yeni ürün oluşturulmaz.',
      exampleRow: 'Örnek satırı değiştirin veya silin.',
      stockNotManaged: 'Bu ürün için stok yönetilmiyor.',
      batchRequired: 'Bu ürün parti numarası gerektirir.',
      batchNotManaged: 'Bu ürün partileri desteklemiyor.',
      mhdRequired: 'Bu ürün tavsiye edilen tüketim tarihi gerektirir.',
      mhdInvalid: 'Tüketim tarihi geçersiz. YYYY-MM-DD kullanın.',
      quantityInvalid: 'Miktar pozitif bir sayı olmalıdır.',
      quantityPrecision: 'Miktarda çok fazla ondalık basamak var.',
      quantityOverflow: 'Dönüştürülen miktar çok büyük.',
      locationInvalid: 'Depo veya konum geçersiz.',
      customFieldMissing: 'Gerekli parti ek alanı eksik.',
      customFieldInvalid: 'Bir parti ek alanı geçersiz.',
      customFieldForbidden: 'Bu parti ek alanını düzenleyemezsiniz.',
      batchConflict: 'Bu bilgiler mevcut partiyle çelişiyor.',
      duplicateBatchConflict: 'Birden fazla satır aynı parti için çelişkili bilgiler içeriyor.',
      inventoryFrozen: 'Devam eden sayım bu ürünü kilitledi.',
      metadataChanged: 'Ürün, parti veya ek alan bilgileri değişti. Dosyayı tekrar kontrol edin.',
      previewInvalid: 'Önizleme hatalar içeriyor.',
      previewChanged: 'Önizleme güncel değil. Dosyayı tekrar kontrol edin.',
      alreadyImported: 'Bu dosya zaten içe aktarıldı.',
      fileInvalid: 'Geçerli bir XLSX dosyası kullanın.',
      fileTooLarge: 'Dosya çok büyük.',
      tooManyRows: 'Dosyada çok fazla satır var.',
      headerInvalid: 'Sütun başlıkları geçersiz.',
      fileEmpty: 'Dosyada veri satırı yok.',
      formulaNotAllowed: 'Formüllere izin verilmez. Sabit değer kullanın.',
      invalidFile: 'Geçerli bir XLSX dosyası kullanın.',
      worksheetCount: 'Dosya tam olarak bir çalışma sayfası içermelidir.',
      requiredColumns: 'Ürün numarası ve miktar sütunları zorunludur.',
      duplicateColumn: 'Bir sütun başlığı yinelenmiş.',
      unknownColumn: 'Bir sütun başlığı bilinmiyor.',
      missingColumnHeader: 'Dolu sütunun başlığı eksik.',
      emptyFile: 'Dosyada veri satırı yok.',
    },
  },
} as const satisfies Record<SupportedLocale, Record<string, string | Record<string, string>>>;
